import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { PanelLeft, Zap, Thermometer, Droplets, Sun, Settings, TrendingUp, AlertTriangle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    toast
  } = useToast();
  useEffect(() => {
    fetchHomeData();
  }, []);
  const fetchHomeData = async () => {
    try {
      setIsLoading(true);

      // Get yesterday's date for the API call
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const startDate = yesterday.toISOString().split('T')[0] + ' 00:00:00';
      const endDate = yesterday.toISOString().split('T')[0] + ' 23:59:59';
      console.log('Fetching home data with params:', {
        startDate,
        endDate,
        plant_id: API_CONFIG.PLANT_ID
      });

      // Fetch energy loss data
      const energyLossResponse = await apiService.getForEnergyLoss({
        start_date: startDate,
        end_date: endDate,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });
      console.log('Energy Loss Response:', energyLossResponse);

      // Fetch PVSyst data for performance ratio
      const pvSystResponse = await apiService.getPvSystData({
        plant_id: API_CONFIG.PLANT_ID
      });
      console.log('PVSyst Response:', pvSystResponse);

      // Fetch graph data for actual performance ratio
      const graphResponse = await apiService.getForGraph({
        start_date: startDate,
        end_date: endDate,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });
      console.log('Graph Response:', graphResponse);
      let energyLossData = null;
      let pvSystData = null;
      let graphData = null;

      // Parse energy loss data (supports both array and wrapped body)
      const energyLossArray = Array.isArray(energyLossResponse.data) ? energyLossResponse.data : energyLossResponse.data?.body || [];
      if (energyLossArray.length > 0 && energyLossArray[0].readings && energyLossArray[0].readings.length > 0) {
        energyLossData = energyLossArray[0].readings[0].EYA;
        console.log('Parsed Energy Loss Data:', energyLossData);
      }

      // Parse PVSyst data
      if (pvSystResponse.data && pvSystResponse.data.json_object) {
        pvSystData = pvSystResponse.data.json_object;
        console.log('Parsed PVSyst Data:', pvSystData);
      }

      // Parse graph data (supports both array and wrapped body)
      const graphArray = Array.isArray(graphResponse.data) ? graphResponse.data : graphResponse.data?.body || [];
      if (graphArray.length > 0 && graphArray[0].readings && graphArray[0].readings.length > 0 && graphArray[0].readings[0].KPI && graphArray[0].readings[0].KPI.length > 0) {
        graphData = graphArray[0].readings[0];
        console.log('Parsed Graph Data:', graphData);
      }

      // Calculate performance ratio from PVSyst data
      let performanceRatioPvsyst = 0;
      if (pvSystData && pvSystData.PVSyst) {
        const yearMonth = new Date().getFullYear() + '-' + (new Date().getMonth() + 1).toString().padStart(2, '0');
        const performanceData = pvSystData.PVSyst.find(item => item.code === "PLANT_PERFORMANCE_RATIO_PVSYST");
        performanceRatioPvsyst = performanceData?.valueMatrix[yearMonth] || 0;
      }

      // Get actual performance ratio from graph data
      let performanceRatioValueActual = 0;
      if (graphData && graphData.KPI && graphData.KPI.length > 0) {
        const performanceRatio = graphData.KPI.find(kpi => kpi.hasOwnProperty('performance_ratio'));
        performanceRatioValueActual = performanceRatio?.performance_ratio || 0;
      }

      // Calculate difference
      const difference = performanceRatioValueActual - performanceRatioPvsyst;
      const differenceResult = difference > 0 ? 'better' : difference < 0 ? 'worse' : 'same';

      // Process energy loss data
      const processedData = {
        performanceRatioValueActual,
        performanceRatioPvsyst,
        difference,
        differenceResult,
        pvModuleTemperatureLoss: energyLossData?.pvmodule_temperature_loss || 0,
        soilingloss: Math.abs(energyLossData?.soiling_loss || 0),
        irradiationloss: energyLossData?.irradiation_loss || 0,
        inverterefficiencyloss: energyLossData?.inverter_efficiency_loss || 0,
        inverterclipping: energyLossData?.plant_clipping_loss_percentage || 0,
        inverterbreakdown: energyLossData?.plant_breakdown_loss_kwh || 0,
        recoverableEnergy: 0 // This would be calculated from the energy loss data
      };
      setHomeData(processedData);
    } catch (error) {
      console.error('Error fetching home data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch home data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const formatNumber = num => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const getPerformanceColor = difference => {
    if (difference > 0) return 'text-green-600 dark:text-green-400';
    if (difference < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };
  const getPerformanceIcon = difference => {
    if (difference > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (difference < 0) return <AlertTriangle className="h-4 w-4 text-red-500" />;
    return <Settings className="h-4 w-4 text-gray-500" />;
  };
  return <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Home Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
            Overview of plant performance and key metrics
          </p>
        </div>
        <Button onClick={fetchHomeData} disabled={isLoading} className="w-full sm:w-auto">
          Refresh Data
        </Button>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plant Performance */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Plant Performance</CardTitle>
            <PanelLeft className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div> : homeData ? <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Plant is{' '}
                  <span className={`font-semibold ${getPerformanceColor(homeData.difference)}`}>
                    {homeData.differenceResult} performing
                  </span>
                  {' '}by {formatNumber(homeData.difference)}% based on actual PR{' '}
                  {formatNumber(homeData.performanceRatioValueActual)}% with reference to PVSyst target value{' '}
                  {formatNumber(homeData.performanceRatioPvsyst)}%
                </p>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  {getPerformanceIcon(homeData.difference)}
                  <span>Performance ratio analysis</span>
                </div>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>

        {/* Recoverable Energy */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Recoverable Energy</CardTitle>
            <Zap className="h-5 w-5 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(homeData.recoverableEnergy || 0)} MWh
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total recoverable energy from system losses
                </p>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* PV Module Temperature Loss */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">PV Module Temperature Loss</CardTitle>
            <Thermometer className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(homeData.pvModuleTemperatureLoss || 0)} MWh
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Temperature-related energy loss
                </p>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>

        {/* Soiling Loss */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Soiling Loss</CardTitle>
            <Droplets className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(homeData.soilingloss || 0)}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Cumulative soiling loss
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  View Details
                </Button>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>

        {/* Irradiation Loss */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Irradiation Loss</CardTitle>
            <Sun className="h-5 w-5 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(homeData.irradiationloss || 0)}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Cumulative irradiation loss
                </p>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>

        {/* Inverter Efficiency */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Inverter Efficiency</CardTitle>
            <Settings className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {homeData.inverterefficiencyloss ? formatNumber(homeData.inverterefficiencyloss) : 'N/A'}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Inverter efficiency loss
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  View Details
                </Button>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>

        {/* Inverter Clipping */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Inverter Clipping</CardTitle>
            <Settings className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {homeData.inverterclipping ? formatNumber(homeData.inverterclipping) : 'N/A'}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Inverter clipping loss
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  View Details
                </Button>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>

        {/* Inverter Breakdown */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Inverter Breakdown Loss</CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : homeData ? <div className="space-y-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {homeData.inverterbreakdown ? formatNumber(homeData.inverterbreakdown) : 'N/A'} kWh
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Inverter breakdown loss
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  View Details
                </Button>
              </div> : <p className="text-gray-500">No data available</p>}
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default Home;